import React from 'react';
import { Dropdown, IDropdownOption, Stack } from '@fluentui/react';
import {
    useGetFolioVendorFriendlyNameQuery,
    useSearchFolioVendorsQuery
} from '../../store/endpoints/RecordLinksEndpoints';

interface IFolioVendorPickerProps {
    folioId: string;
    folioName?: string;
    /** Currently mapped external vendor id, if any. */
    value?: string;
    onChange: (externalId: string | undefined) => void;
}

/**
 * Dropdown that maps the current Adaptive Catalog vendor to a CRM/PSA vendor for a single folio.
 * Loads the folio's vendor list through the extension, and resolves the display name of an
 * already-mapped id via the folio's friendly-name lookup so the saved selection always shows.
 */
const FolioVendorPicker: React.FC<IFolioVendorPickerProps> = ({folioId, folioName, value, onChange}) => {
    const {data, isFetching} = useSearchFolioVendorsQuery({folioId});
    const friendlyName = useGetFolioVendorFriendlyNameQuery({folioId, externalId: value ?? ''}, {skip: !value});

    // Greyed out while the vendor list loads from the extension, or while we resolve the friendly
    // name of an already-mapped vendor.
    const isLoading = isFetching || (!!value && friendlyName.isFetching);

    // Build the option list, ensuring the current mapping is always present so it stays selected
    // even if it isn't returned in the vendor list.
    const options: IDropdownOption[] = React.useMemo(() => {
        const opts: IDropdownOption[] = (data?.options ?? []).map(o => ({key: o.value, text: o.display}));
        if (value && !opts.some(o => o.key === value)) {
            opts.unshift({key: value, text: isLoading ? `Loading (${value})` : (friendlyName.data || value)});
        }
        // Allow clearing an existing mapping.
        opts.unshift({key: '', text: '(None)'});
        return opts;
    }, [data, value, friendlyName.data, isLoading]);

    return (
        <Stack horizontal tokens={{childrenGap: '0.5em'}} verticalAlign='end'>
            <Dropdown
                styles={{root: {width: 300}}}
                label={folioName}
                placeholder={isLoading ? (value ? `Loading (${value})` : 'Loading…') : 'Select a CRM vendor…'}
                selectedKey={value ?? null}
                options={options}
                disabled={isLoading}
                notifyOnReselect
                onChange={(_e, option) => onChange(option?.key ? (option.key as string) : undefined)}
            />
        </Stack>
    );
};

export default FolioVendorPicker;
